import { User } from '../types/user';

export interface UsageTier {
  name: string;
  maxCost: number;
  pricePerMonth: number;
  features: string[];
  aiAccess: boolean;
}

export interface UsageStats {
  totalCost: number;
  requestCount: number;
  lastRequest: number;
  tier: string;
}

const COST_PER_REQUEST = 0.0001; // $0.0001 per request (DeepSeek is much cheaper)

export const PRICING_TIERS: UsageTier[] = [
  {
    name: 'Free',
    maxCost: 1.00, // $1 worth of AI usage for free tier
    pricePerMonth: 0,
    aiAccess: true,
    features: [
      'Basic task tracking',
      'Simple pomodoro timer',
      'Standard statistics',
      'Community access',
      'AI-powered insights (limited)',
      'Smart task suggestions'
    ]
  },
  {
    name: 'Enterprise',
    maxCost: 3.00, // $3 worth of AI usage
    pricePerMonth: 9.99, // We charge $9.99, cost is max $3 = ~70% margin
    aiAccess: true,
    features: [
      'Everything in Free plan',
      'AI-powered insights',
      'Smart task suggestions',
      'Focus quality analysis',
      'Break optimization',
      'Advanced analytics',
      'Priority support'
    ]
  }
];

class PricingService {
  private usageMap = new Map<string, UsageStats>();

  constructor() {
    try {
      const savedUsage = localStorage.getItem('aiUsage');
      if (savedUsage) {
        const parsed = JSON.parse(savedUsage);
        Object.entries(parsed).forEach(([userId, stats]) => {
          this.usageMap.set(userId, stats as UsageStats);
        });
      }
    } catch (error) {
      console.error('Error loading usage data:', error);
    }
  }

  private saveUsage() {
    try {
      const usageObject = Object.fromEntries(this.usageMap);
      localStorage.setItem('aiUsage', JSON.stringify(usageObject));
    } catch (error) {
      console.error('Error saving usage data:', error);
    }
  }

  getUserTier(userId: string): UsageTier {
    const usage = this.usageMap.get(userId);
    if (!usage) return PRICING_TIERS[0]; // Free tier

    const currentTier = PRICING_TIERS.find(tier => tier.name === usage.tier);
    return currentTier || PRICING_TIERS[0];
  }

  async checkUsageLimit(userId: string): Promise<{ allowed: boolean; reason?: string }> {
    const usage = this.usageMap.get(userId);
    const tier = this.getUserTier(userId);

    if (!tier.aiAccess) {
      return {
        allowed: false,
        reason: 'AI features are only available in the Enterprise plan. Upgrade to access AI-powered insights and suggestions.'
      };
    }
    
    if (!usage) {
      return { allowed: true };
    }

    const newCost = usage.totalCost + COST_PER_REQUEST;
    if (newCost > tier.maxCost) {
      return {
        allowed: false,
        reason: `You've reached your Enterprise plan limit. Contact us to increase your usage limit.`
      };
    }

    return { allowed: true };
  }

  async recordUsage(userId: string): Promise<void> {
    let usage = this.usageMap.get(userId);
    
    if (!usage) {
      usage = {
        totalCost: 0,
        requestCount: 0,
        lastRequest: Date.now(),
        tier: 'Enterprise'
      };
    }

    usage.totalCost += COST_PER_REQUEST;
    usage.requestCount += 1;
    usage.lastRequest = Date.now();

    this.usageMap.set(userId, usage);
    this.saveUsage();
  }

  async upgradeTier(userId: string, newTier: string): Promise<boolean> {
    const tier = PRICING_TIERS.find(t => t.name === newTier);
    if (!tier) return false;

    let usage = this.usageMap.get(userId);
    if (!usage) {
      usage = {
        totalCost: 0,
        requestCount: 0,
        lastRequest: Date.now(),
        tier: newTier
      };
    } else {
      usage.tier = newTier;
    }

    this.usageMap.set(userId, usage);
    this.saveUsage();
    return true;
  }

  getUsageStats(userId: string): UsageStats {
    return (
      this.usageMap.get(userId) || {
        totalCost: 0,
        requestCount: 0,
        lastRequest: Date.now(),
        tier: 'Free'
      }
    );
  }

  getRemainingUsage(userId: string): number {
    const usage = this.usageMap.get(userId);
    const tier = this.getUserTier(userId);
    
    if (!tier.aiAccess) return 0;
    if (!usage) return tier.maxCost;
    return Math.max(0, tier.maxCost - usage.totalCost);
  }

  async increaseUsageLimit(userId: string, additionalAmount: number): Promise<boolean> {
    const usage = this.usageMap.get(userId);
    if (!usage || usage.tier !== 'Enterprise') return false;

    const tier = this.getUserTier(userId);
    const newMaxCost = tier.maxCost + additionalAmount;
    
    // Custom pricing: $9.99 base + $3.33 per additional $1 of usage
    const newMonthlyPrice = 9.99 + (additionalAmount * 3.33);

    // Create custom tier for this user
    const customTier: UsageTier = {
      name: 'Enterprise',
      maxCost: newMaxCost,
      pricePerMonth: newMonthlyPrice,
      aiAccess: true,
      features: [
        ...PRICING_TIERS[1].features,
        `Increased AI usage limit ($${newMaxCost.toFixed(2)})`
      ]
    };

    // Update the pricing tiers for this user
    PRICING_TIERS[1] = customTier;
    
    return true;
  }
}

export const pricingService = new PricingService(); 